import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, FileText, Home, Mail, MessageCircle, Settings } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import "../../styles/diy-animations.css";

const bottomNav: { to: string; label: string; Icon: React.ComponentType<{ className?: string; strokeWidth?: number }> }[] = [
  { to: "/diy/dashboard", label: "Dashboard", Icon: Home },
  { to: "/diy/my-disputes", label: "Disputes", Icon: FileText },
  { to: "/diy/my-letters", label: "Letters", Icon: Mail },
  { to: "/diy/ai-coach", label: "AI Coach", Icon: MessageCircle },
  { to: "/diy/settings", label: "Settings", Icon: Settings },
];

const DiyLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const initial = (user?.user?.firstName?.[0] ?? user?.operatorEmail?.[0] ?? "U").toUpperCase();

  return (
    <div
      className="diy-landing min-h-screen text-slate-100"
      style={{ backgroundColor: "#0B1120", fontFamily: "'DM Sans', sans-serif" }}
    >
      <header
        className="fixed left-0 right-0 top-0 z-50 flex h-16 items-center justify-between border-b px-6"
        style={{
          background: "rgba(11, 17, 32, 0.95)",
          borderColor: "rgba(255, 255, 255, 0.06)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        <Link to="/diy/dashboard" className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ background: "linear-gradient(135deg, #10B981, #0EA5E9)" }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 3L5 6v5c0 4.5 3.2 8.7 7 10 3.8-1.3 7-5.5 7-10V6l-7-3z"
                stroke="white"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span
            style={{
              fontFamily: "'Sora', sans-serif",
              fontWeight: 700,
              fontSize: 16,
              color: "#F1F5F9",
              letterSpacing: "-0.5px",
            }}
          >
            IntelliWarrior
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="rounded-lg border-0 bg-transparent p-1 transition-colors duration-200"
            style={{ color: "#94A3B8" }}
            aria-label="Notifications"
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#F1F5F9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#94A3B8";
            }}
          >
            <Bell className="h-5 w-5" strokeWidth={2} />
          </button>
          <Link
            to="/diy/settings"
            className="flex h-8 w-8 items-center justify-center rounded-full text-white no-underline transition-opacity hover:opacity-90"
            style={{
              background: "linear-gradient(135deg, #10B981, #0EA5E9)",
              fontFamily: "'Sora', sans-serif",
              fontWeight: 700,
              fontSize: 12,
            }}
            aria-label="Account settings"
          >
            {initial}
          </Link>
        </div>
      </header>

      <main className="min-h-screen overflow-x-hidden px-5 pb-20 pt-20">
        {children}
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t px-2"
        style={{
          background: "rgba(11, 17, 32, 0.95)",
          borderColor: "rgba(255, 255, 255, 0.06)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        {bottomNav.map(({ to, label, Icon }) => {
          const active =
            to === "/diy/dashboard"
              ? location.pathname === "/diy/dashboard" || location.pathname === "/diy"
              : location.pathname === to || location.pathname.startsWith(`${to}/`);
          return (
            <Link
              key={to}
              to={to}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 py-1 no-underline transition-colors duration-200"
              style={{ color: active ? "#10B981" : "#64748B" }}
            >
              <span className="relative flex h-6 items-end justify-center">
                {active ? (
                  <span
                    className="absolute -top-1 h-1 w-1 rounded-full"
                    style={{ background: "#10B981", boxShadow: "0 0 6px rgba(16,185,129,0.8)" }}
                  />
                ) : null}
                <Icon className="h-[22px] w-[22px]" strokeWidth={2} />
              </span>
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 500,
                  fontSize: 10,
                }}
              >
                {label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default DiyLayout;
